import { useEffect, useState } from "react";
import axios from "axios";
import { DollarSign, Activity, Zap, AlertTriangle, Loader2, TrendingUp, Server, RefreshCw, Clock } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtRel = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  const diffMin = Math.floor((Date.now() - d.getTime()) / 60000);
  if (diffMin < 1) return "ora";
  if (diffMin < 60) return `${diffMin} min fa`;
  const h = Math.floor(diffMin / 60);
  if (h < 24) return `${h}h fa`;
  const days = Math.floor(h / 24);
  return `${days}g fa`;
};

/**
 * LLM Costs Dashboard
 * Mostra costi LLM aggregati: totali, per scheduler, per modello, trend giornaliero, recent failures.
 * Polling on demand (refresh manuale + auto 60s).
 * Aggiornato (Livello A): badge "Ultimo aggiornamento" + bottone forza refresh aggregator.
 */
export default function LlmCostsTab() {
  const [data, setData] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [days, setDays] = useState(7);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const fetchData = async (d = days) => {
    try {
      const [r1, r2] = await Promise.all([
        axios.get(`${API}/admin/llm-costs/summary?days=${d}`, { withCredentials: true }),
        axios.get(`${API}/admin/llm-costs/alerts`, { withCredentials: true }).catch(() => ({ data: { alerts: [] } })),
      ]);
      setData(r1.data);
      setAlerts(r2.data?.alerts || []);
      setError(null);
    } catch (e) {
      setError(e?.response?.data?.detail || "Errore caricamento costi LLM");
    } finally {
      setLoading(false);
    }
  };

  const ackAlerts = async () => {
    try {
      await axios.post(`${API}/admin/llm-costs/alerts/ack`, {}, { withCredentials: true });
      setAlerts([]);
    } catch (e) {
      setError("Errore ack alert");
    }
  };

  const forceRefresh = async () => {
    if (refreshing) return;
    if (!window.confirm("Vuoi aggregare il giorno precedente ora?")) return;
    setRefreshing(true);
    try {
      const { data: r } = await axios.post(`${API}/admin/llm-costs/refresh-now`, {}, { withCredentials: true });
      if (r.outcome === "ok") {
        toast.success(`Aggregazione OK — ${r.totals?.calls || 0} chiamate ($${(r.totals?.cost_usd || 0).toFixed(4)})`);
      } else {
        toast.error(`Aggregazione fallita: ${r.error || "errore sconosciuto"}`);
      }
      await fetchData(days);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore force refresh");
    } finally {
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchData(days);
    const id = setInterval(() => fetchData(days), 60000);
    return () => clearInterval(id);
    // eslint-disable-next-line
  }, [days]);

  if (loading && !data) {
    return (
      <div className="flex items-center gap-2 text-zinc-500" data-testid="llm-costs-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> Caricamento costi LLM…
      </div>
    );
  }

  const t = data?.totals || {};
  const fmtUSD = (v) => `$${(v || 0).toFixed(4)}`;
  const fmtTokens = (v) => (v || 0) >= 1000 ? `${((v || 0) / 1000).toFixed(1)}k` : `${v || 0}`;
  const maxDailyCost = Math.max(...(data?.trend_daily || []).map(x => x.cost_usd || 0), 0.0001);
  const maxScheCost = Math.max(...(data?.by_scheduler || []).map(x => x.cost_usd || 0), 0.0001);

  return (
    <div className="space-y-6 max-w-6xl" data-testid="admin-llm-costs-tab">
      {/* Header */}
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h3 className="text-lg font-semibold text-zinc-900 mb-1 flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-emerald-600" />
            Costi LLM (Universal Key)
          </h3>
          <p className="text-sm text-zinc-500">
            Tracking automatico di tutte le chiamate AI: scheduler news, alert engine, OCR documenti.
            Stima token con regola 4 char/token. Pricing ballpark in USD/1M tokens.
          </p>
          {/* Badge ultimo aggiornamento (Livello A) */}
          {(() => {
            const last = data?.last_aggregated_at;
            const outcome = data?.last_aggregated_outcome;
            const minutesAgo = last ? Math.floor((Date.now() - new Date(last).getTime()) / 60000) : null;
            const isLate = minutesAgo !== null && minutesAgo > 24 * 60;
            const isFailed = outcome === "failed";
            let cls = "bg-emerald-50 text-emerald-800 border-emerald-200";
            let label = "Aggregato";
            if (isFailed) { cls = "bg-red-50 text-red-800 border-red-200"; label = "Aggregazione fallita"; }
            else if (isLate) { cls = "bg-amber-50 text-amber-800 border-amber-200"; label = "Aggregazione in ritardo"; }
            else if (!last) { cls = "bg-zinc-50 text-zinc-700 border-zinc-200"; label = "Mai aggregato"; }
            return (
              <div className="flex items-center gap-2 mt-2" data-testid="llm-costs-last-aggregated">
                <span className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full border ${cls}`}>
                  <Clock className="w-3 h-3" />
                  {label}: {fmtRel(last)}
                  {data?.last_aggregated_covers_date && ` (${data.last_aggregated_covers_date})`}
                </span>
                <button
                  onClick={forceRefresh}
                  disabled={refreshing}
                  className="inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full border bg-white text-zinc-700 border-zinc-200 hover:bg-zinc-50 disabled:opacity-50"
                  data-testid="llm-costs-force-refresh"
                  title="Forza aggregazione del giorno precedente"
                >
                  <RefreshCw className={`w-3 h-3 ${refreshing ? "animate-spin" : ""}`} />
                  Forza aggiornamento
                </button>
              </div>
            );
          })()}
        </div>
        <div className="flex items-center gap-2">
          {[1, 7, 14, 30].map(d => (
            <button
              key={d}
              onClick={() => setDays(d)}
              className={`text-xs px-3 py-1.5 rounded-md border transition ${days === d ? "bg-zinc-900 text-white border-zinc-900" : "bg-white text-zinc-600 border-zinc-200 hover:border-zinc-400"}`}
              data-testid={`llm-costs-period-${d}d`}
            >
              {d === 1 ? "Oggi" : `${d}gg`}
            </button>
          ))}
        </div>
      </div>

      {error && (
        <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md p-3" data-testid="llm-costs-error">
          {error}
        </div>
      )}

      {/* Active alerts banner */}
      {alerts.length > 0 && (
        <div className="bg-red-50 border-2 border-red-300 rounded-lg p-4 space-y-2" data-testid="llm-active-alerts">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-6 h-6 text-red-600 shrink-0 mt-0.5" />
              <div>
                <h4 className="font-bold text-red-900">Soglia costi LLM superata</h4>
                <p className="text-sm text-red-700 mt-0.5">
                  {alerts.length} {alerts.length === 1 ? "allerta attiva" : "allerte attive"}. Verifica eventuali loop o scheduler runaway.
                </p>
              </div>
            </div>
            <button
              onClick={ackAlerts}
              className="text-xs px-3 py-1.5 rounded-md bg-red-600 text-white hover:bg-red-700 shrink-0"
              data-testid="llm-alerts-ack"
            >
              Ho letto, dismiss
            </button>
          </div>
          <div className="space-y-1 pl-9 text-sm">
            {alerts.slice(0, 5).map((a, i) => (
              <div key={i} className="font-mono text-xs text-red-800">
                <span className="text-red-500">{(a.ts || "").slice(0, 19).replace("T", " ")}</span>
                {" · "}<b>${(a.spent_usd || 0).toFixed(3)}</b> in 24h (soglia ${(a.threshold_usd || 0).toFixed(2)})
                {" · "}{a.calls} chiamate
                {a.top_schedulers && <> · top: <span className="text-red-700">{a.top_schedulers}</span></>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiCard
          icon={<DollarSign className="w-4 h-4" />}
          label="Spesa stimata"
          value={fmtUSD(t.cost_usd)}
          accent="text-emerald-600"
          testid="llm-kpi-cost"
        />
        <KpiCard
          icon={<Activity className="w-4 h-4" />}
          label="Chiamate"
          value={t.calls || 0}
          sub={`${t.calls_ok || 0} OK • ${t.success_rate ?? 100}% success`}
          accent="text-zinc-900"
          testid="llm-kpi-calls"
        />
        <KpiCard
          icon={<Zap className="w-4 h-4" />}
          label="Token totali"
          value={fmtTokens(t.tokens_total)}
          sub={`in ${fmtTokens(t.tokens_in)} • out ${fmtTokens(t.tokens_out)}`}
          accent="text-amber-600"
          testid="llm-kpi-tokens"
        />
        <KpiCard
          icon={<Server className="w-4 h-4" />}
          label="Latenza media"
          value={`${t.avg_duration_ms || 0} ms`}
          accent="text-sky-600"
          testid="llm-kpi-latency"
        />
      </div>

      {/* By Scheduler */}
      <div className="bg-white border border-zinc-200 rounded-lg p-4" data-testid="llm-by-scheduler">
        <h4 className="text-sm font-semibold text-zinc-900 mb-3 flex items-center gap-2">
          <TrendingUp className="w-4 h-4 text-emerald-600" />
          Costi per scheduler
        </h4>
        {(data?.by_scheduler || []).length === 0 ? (
          <p className="text-sm text-zinc-500">Nessuna chiamata registrata in questo periodo.</p>
        ) : (
          <div className="space-y-2">
            {data.by_scheduler.map(s => (
              <div key={s.scheduler} className="flex items-center gap-3" data-testid={`llm-sched-${s.scheduler}`}>
                <div className="w-44 text-sm text-zinc-700 font-medium">{s.scheduler}</div>
                <div className="flex-1 h-6 bg-zinc-100 rounded overflow-hidden relative">
                  <div
                    className="h-full bg-gradient-to-r from-emerald-400 to-emerald-600 transition-all"
                    style={{ width: `${(s.cost_usd / maxScheCost) * 100}%` }}
                  />
                </div>
                <div className="w-24 text-right text-sm font-mono text-emerald-700">{fmtUSD(s.cost_usd)}</div>
                <div className="w-20 text-right text-xs text-zinc-500">{s.calls} call</div>
                {s.fails > 0 && (
                  <div className="w-12 text-right text-xs text-red-600">{s.fails} fail</div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* By Model */}
      <div className="bg-white border border-zinc-200 rounded-lg p-4" data-testid="llm-by-model">
        <h4 className="text-sm font-semibold text-zinc-900 mb-3">Per modello</h4>
        {(data?.by_model || []).length === 0 ? (
          <p className="text-sm text-zinc-500">—</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-zinc-500 text-xs uppercase tracking-wide">
                  <th className="text-left py-2">Provider / Model</th>
                  <th className="text-right py-2">Chiamate</th>
                  <th className="text-right py-2">Token</th>
                  <th className="text-right py-2">Costo USD</th>
                </tr>
              </thead>
              <tbody>
                {data.by_model.map((m, i) => (
                  <tr key={i} className="border-t border-zinc-100">
                    <td className="py-2 font-medium text-zinc-800">
                      <span className="text-zinc-500">{m.provider}</span> · <span>{m.model}</span>
                    </td>
                    <td className="py-2 text-right">{m.calls}</td>
                    <td className="py-2 text-right text-zinc-600">{fmtTokens(m.tokens)}</td>
                    <td className="py-2 text-right font-mono text-emerald-700">{fmtUSD(m.cost_usd)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Daily Trend */}
      <div className="bg-white border border-zinc-200 rounded-lg p-4" data-testid="llm-trend">
        <h4 className="text-sm font-semibold text-zinc-900 mb-3">Trend giornaliero</h4>
        {(data?.trend_daily || []).length === 0 ? (
          <p className="text-sm text-zinc-500">Nessun dato.</p>
        ) : (
          <div className="flex items-end gap-1 h-32">
            {data.trend_daily.map(d => (
              <div key={d.date} className="flex-1 flex flex-col items-center gap-1 group">
                <div className="text-[10px] text-zinc-400 group-hover:text-zinc-700 transition">
                  {fmtUSD(d.cost_usd)}
                </div>
                <div
                  className="w-full bg-emerald-500/80 hover:bg-emerald-600 transition rounded-t"
                  style={{ height: `${Math.max(2, (d.cost_usd / maxDailyCost) * 100)}%` }}
                  title={`${d.date}: ${fmtUSD(d.cost_usd)} • ${d.calls} chiamate`}
                />
                <div className="text-[10px] text-zinc-500 truncate w-full text-center">{d.date.slice(5)}</div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Recent Failures */}
      {(data?.recent_failures || []).length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4" data-testid="llm-failures">
          <h4 className="text-sm font-semibold text-red-800 mb-3 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />
            Ultimi fallimenti ({data.recent_failures.length})
          </h4>
          <div className="space-y-1.5 text-xs">
            {data.recent_failures.map((f, i) => (
              <div key={i} className="flex items-start gap-2 font-mono">
                <span className="text-red-500 shrink-0">{(f.ts || "").slice(11, 19)}</span>
                <span className="text-zinc-700 shrink-0">{f.scheduler}</span>
                <span className="text-zinc-500 shrink-0">{f.provider}/{f.model}</span>
                <span className="text-red-700 truncate">{f.error || "—"}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <p className="text-xs text-zinc-400 italic">
        Auto-refresh ogni 60s · Dati conservati 60 giorni · Stima costo basata su pricing pubblico provider Apr 2026 (±20% variabilità).
      </p>
    </div>
  );
}

function KpiCard({ icon, label, value, sub, accent = "text-zinc-900", testid }) {
  return (
    <div className="bg-white border border-zinc-200 rounded-lg p-4" data-testid={testid}>
      <div className="flex items-center justify-between text-zinc-500 text-xs mb-1">
        <span className="uppercase tracking-wide">{label}</span>
        <span className={accent}>{icon}</span>
      </div>
      <div className={`text-2xl font-bold ${accent}`}>{value}</div>
      {sub && <div className="text-xs text-zinc-500 mt-1">{sub}</div>}
    </div>
  );
}
